<template>
  <!-- 리뷰 사진 모달 컴포넌트 -->
  <div
    v-if="isVisible"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
    @click="closeModal"
  >
    <!-- 모달 콘텐츠 -->
    <div class="relative w-full h-full flex items-center justify-center p-4">
      <!-- 이전 사진 버튼 -->
      <button
        v-if="photos.length > 1"
        @click.stop
        @click="goToPreviousPhoto"
        class="absolute left-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
        aria-label="이전 사진"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M15 18L9 12L15 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- 다음 사진 버튼 -->
      <button
        v-if="photos.length > 1"
        @click.stop
        @click="goToNextPhoto"
        class="absolute right-4 top-1/2 transform -translate-y-1/2 z-10 text-white hover:text-gray-300 transition-colors bg-black bg-opacity-50 rounded-full p-2"
        aria-label="다음 사진"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M9 18L15 12L9 6"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <!-- 메인 사진 -->
      <div class="flex items-center justify-center w-full h-full">
        <div
          v-if="currentPhoto"
          class="relative flex items-start justify-center w-fit h-fit bg-white"
        >
          <!-- 닫기 버튼 -->
          <button
            @click="closeModal"
            class="absolute top-4 right-4 z-10 text-black hover:text-gray-300 transition-colors"
            aria-label="모달 닫기"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M6 6L18 18"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <div class="w-[25rem] h-[25rem]">
            <img
              v-if="currentPhoto"
              :src="getPhotoUrl(currentPhoto)"
              :alt="`리뷰 사진 ${currentIndex + 1}`"
              class="max-w-full max-h-full object-contain"
              @error="handleImageError"
            />
          </div>

          <div class="w-[25rem] h-full pt-12 p-5">
            <p>리뷰제목</p>
            <p class="w-full mt-3 pt-3 border-t border-neutral-200">리뷰내용</p>
          </div>
        </div>

        <!-- 이미지 로딩 실패 시 표시 -->
        <div v-else-if="imageError" class="text-white text-center p-8">
          <p class="text-lg mb-2">이미지를 불러올 수 없습니다</p>
          <p class="text-sm text-gray-300">잠시 후 다시 시도해주세요</p>
        </div>
      </div>

      <!-- 사진 정보 -->
      <div
        v-if="photos.length > 1"
        class="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 rounded-full px-4 py-2"
      >
        {{ currentIndex + 1 }} / {{ photos.length }}
      </div>

      <!-- 썸네일 네비게이션 (사진이 많을 때) -->
      <div
        v-if="photos.length > 1 && photos.length <= 10"
        class="absolute bottom-16 left-1/2 transform -translate-x-1/2 flex gap-2"
      >
        <button
          v-for="(photo, index) in photos"
          :key="index"
          @click="goToPhoto(index)"
          class="w-16 h-16 border-2 rounded overflow-hidden transition-opacity"
          :class="[
            index === currentIndex
              ? 'border-white opacity-100'
              : 'border-gray-400 opacity-70 hover:opacity-90',
          ]"
          :aria-label="`사진 ${index + 1}로 이동`"
        >
          <img
            :src="getPhotoUrl(photo)"
            :alt="`썸네일 ${index + 1}`"
            class="w-full h-full object-cover"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";

// Props 정의
const props = defineProps({
  /** 모달 표시 여부 */
  isVisible: {
    type: Boolean,
    default: false,
  },
  /** 사진 목록 배열 */
  photos: {
    type: Array,
    default: () => [],
  },
  /** 초기 표시할 사진 인덱스 */
  initialIndex: {
    type: Number,
    default: 0,
  },
});

// Emits 정의
const emit = defineEmits(["close", "photoChange"]);

// 반응형 상태
const currentIndex = ref(props.initialIndex);
const imageError = ref(false);

// 계산된 속성
const currentPhoto = computed(() => {
  return props.photos[currentIndex.value] || null;
});

// 메서드들
/**
 * 사진 URL 반환 함수
 * @param {string|Object} photo - 사진 데이터 (URL 문자열 또는 객체)
 * @return {string} 완전한 사진 URL
 */
const getPhotoUrl = (photo) => {
  if (!photo) return "";

  // 문자열인 경우 (URL)
  if (typeof photo === "string") {
    return photo.startsWith("http")
      ? photo
      : `https://eduwind-api.nodeblack.net/${photo}`;
  }

  // 객체인 경우 (url 또는 photoUrl 속성 확인)
  const url = photo.url || photo.photoUrl || photo.imageUrl || "";
  return url.startsWith("http")
    ? url
    : `https://eduwind-api.nodeblack.net/${url}`;
};

/**
 * 모달 닫기 함수
 */
const closeModal = () => {
  emit("close");
};

/**
 * 이전 사진으로 이동
 */
const goToPreviousPhoto = () => {
  if (props.photos.length <= 1) return;

  currentIndex.value =
    currentIndex.value === 0 ? props.photos.length - 1 : currentIndex.value - 1;

  imageError.value = false;
  emit("photoChange", currentIndex.value);
};

/**
 * 다음 사진으로 이동
 */
const goToNextPhoto = () => {
  if (props.photos.length <= 1) return;

  currentIndex.value =
    currentIndex.value === props.photos.length - 1 ? 0 : currentIndex.value + 1;

  imageError.value = false;
  emit("photoChange", currentIndex.value);
};

/**
 * 특정 사진으로 이동
 * @param {number} index - 이동할 사진의 인덱스
 */
const goToPhoto = (index) => {
  if (index >= 0 && index < props.photos.length) {
    currentIndex.value = index;
    imageError.value = false;
    emit("photoChange", index);
  }
};

/**
 * 이미지 로딩 에러 처리
 */
const handleImageError = () => {
  imageError.value = true;
};

/**
 * 키보드 이벤트 핸들러
 * @param {KeyboardEvent} event - 키보드 이벤트
 */
const handleKeydown = (event) => {
  if (!props.isVisible) return;

  switch (event.key) {
    case "Escape":
      closeModal();
      break;
    case "ArrowLeft":
      goToPreviousPhoto();
      break;
    case "ArrowRight":
      goToNextPhoto();
      break;
  }
};

// 라이프사이클 훅
onMounted(() => {
  document.addEventListener("keydown", handleKeydown);
  // 모달이 열릴 때 body 스크롤 방지
  if (props.isVisible) {
    document.body.style.overflow = "hidden";
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  // 컴포넌트 언마운트 시 body 스크롤 복원
  document.body.style.overflow = "";
});

// Props 변경 감지
import { watch } from "vue";

watch(
  () => props.isVisible,
  (newValue) => {
    if (newValue) {
      document.body.style.overflow = "hidden";
      currentIndex.value = props.initialIndex;
      imageError.value = false;
    } else {
      document.body.style.overflow = "";
    }
  }
);

watch(
  () => props.initialIndex,
  (newIndex) => {
    currentIndex.value = newIndex;
    imageError.value = false;
  }
);
</script>

<style scoped>
/* 애니메이션 효과 */
.transition-colors {
  transition: color 0.2s ease-in-out;
}

.transition-opacity {
  transition: opacity 0.2s ease-in-out;
}

/* 스크롤바 숨기기 */
::-webkit-scrollbar {
  display: none;
}
</style>
